// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ListingCategories from "@eisinc/App/Components/ListingCategories/ListingCategories";
import loadKnowledgeCenterItems from "@eisinc/App/Handlers/LoadKnowledgeCenterItems";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { getDocumentsState } from "@eisinc/App/Store/Selectors/DocumentsSelector";
import { CheckboxGroupProps } from "@eisinc/MobiusOverrides/CheckboxGroup/CheckboxGroup";
import Pagination, { PaginationPresentationProps } from "@eisinc/MobiusOverrides/Pagination";
import ProductListFiltersAccordionSection, {
    productListFilterAccordionSectionStyles,
} from "@eisinc/Overrides/Widgets/ProductList/ProductListFilterAccordionSection";
import { screen } from "@eisinc/XDL/Helpers";
import { xScreen } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import { CheckboxPresentationProps } from "@insite/mobius/Checkbox/Checkbox";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import HTMLEllipsis from "react-lines-ellipsis/lib/html";

interface XngFacetModel extends FacetModel {
    value?: string;
}

const mapStateToProps = (state: CustomAppState) => ({
    customSettings: getSettingsCollection(state).customSettings,
    url: `https://${state?.context?.website?.uri?.split("/")[2].split(":")[0]}`,
    urlForDetails: getPageLinkByPageType(state, "KnowledgeCenterDetailsPage"),
    isLoadingDocuments: getDocumentsState(state)?.isLoading,
});

const mapDispatchToProps = {
    loadKnowledgeCenterItems,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface KnowledgeCenterStyles {
    leftContainer?: InjectableCss;
    rightContainer?: InjectableCss;
    filterSection?: InjectableCss;
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
    facetsWrapper?: InjectableCss;
    wrapper?: InjectableCss;
    tableStyle?: InjectableCss;
    topPaginationStyle?: PaginationPresentationProps;
    bottomPaginationStyle?: PaginationPresentationProps;
    paginationWrapper?: InjectableCss;
    totalResultsStyle?: InjectableCss;
    centeringWrapper?: InjectableCss;
    tableWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    tagsDiv?: InjectableCss;
}

export const styles: KnowledgeCenterStyles = {
    rightContainer: {
        css: css`
            border: 1px solid;
        `,
    },
    filterSection: {
        css: css`
            width: 100%;
        `,
    },
    tableStyle: {
        css: css`
            flex-direction: column;
        `,
    },
    tableWrapper: {
        css: css`
            overflow: auto;
            width: 100%;
            overflow-y: hidden;
            margin-top: 8px;
        `,
    },
    paginationWrapper: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
            justify-content: space-between;
            ${screen("lte", "xs")`
                display: block;
            `}
        `,
    },
    topPaginationStyle: {
        mergeCss: true,
        cssOverrides: {
            pagination: css`
                ${xScreen("lte", "md")} {
                    flex-wrap: wrap;
                    row-gap: 8px;
                    justify-content: space-between;
                }
            `,
        },
    },
    bottomPaginationStyle: {
        mergeCss: true,
        cssOverrides: {
            pagination: css`
                ${xScreen("lte", "md")} {
                    flex-wrap: wrap;
                    row-gap: 8px;
                }
            `,
        },
    },
    totalResultsStyle: {
        css: css`
            ${screen("lte", "xs")`
                width: 100%;
                text-align: center;
                margin-bottom: 15px;
            `}
        `,
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        color: "primary.main",
        css: css`
            margin: 0 auto;
        `,
    },
    tagsDiv: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            ${screen("lte", "sm")`
                margin-top: 10px;
            `}
        `,
    },
    ...productListFilterAccordionSectionStyles,
};

const KnowledgeCenter: React.FC<Props> = ({
    loadKnowledgeCenterItems,
    isLoadingDocuments,
    customSettings,
    url,
    urlForDetails,
}) => {
    const defaultPageSize = parseInt((customSettings as any).knowledgeCenterDefaultPageSize, 10);
    const pageSizeOptions = [defaultPageSize, defaultPageSize * 2, defaultPageSize * 3, defaultPageSize * 4];
    const [documents, setDocuments] = useState([]);
    const [categoryFacets, setCategoryFacets] = useState([]);
    const [brandFacets, setBrandFacets] = useState([]);
    const [typeFacets, setTypeFacets] = useState([]);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [totalResults, setTotalResults] = useState(0);
    const [pageSize, setPageSize] = useState(parseInt((customSettings as any).knowledgeCenterDefaultPageSize, 10));
    const [page, setPage] = useState(1);
    const [documentType, setDocumentType] = useState([]);
    const [productCategory, setProductCategory] = useState([]);
    const [productBrand, setProductBrand] = useState([]);
    const [filterChanged, setFilterChanged] = useState({} as any);
    const facetsTypes = {
        types: translate("Types"),
        categories: translate("Categories"),
        brands: translate("Brands"),
    };

    const loadData = () => {
        const params: any = {
            pageSize,
            page,
            documentType,
            productCategory,
            productBrand,
        };

        loadKnowledgeCenterItems({
            params,
            onComplete: (response: any) => {
                const apiResult = response.apiResult;
                if (response.apiResult) {
                    setDocuments(apiResult.documents);
                    setTotalResults(apiResult.totalResults);
                    if (!dataLoaded) {
                        setTypeFacets(apiResult.typeFacets);
                        setBrandFacets(apiResult.brandFacets);
                        setCategoryFacets(apiResult.categoryFacets);
                    }
                    setDataLoaded(true);
                }
            },
        });
    };

    useEffect(() => {
        if (
            filterChanged.name === facetsTypes.types ||
            filterChanged.name === facetsTypes.categories ||
            filterChanged.name === facetsTypes.brands ||
            pageSize > 0 ||
            page > 0
        ) {
            changeBrandsCategoryTypes();
        }
    }, [filterChanged, pageSize, page]);

    const changeBrandsCategoryTypes = () => {
        const params: any = {
            pageSize,
            page,
            documentType,
            productCategory,
            productBrand,
        };
        loadKnowledgeCenterItems({
            params,
            onComplete: (response: any) => {
                const apiResult = response.apiResult;
                if (response.apiResult) {
                    setDocuments(apiResult.documents);
                    setTotalResults(apiResult.totalResults);

                    apiResult.brandFacets.forEach((newFacet: any) => {
                        brandFacets.forEach((oldFacet: any) => {
                            if (newFacet.name === oldFacet.name && oldFacet.selected) {
                                newFacet.selected = true;
                            }
                        });
                    });

                    apiResult.categoryFacets.forEach((newFacet: any) => {
                        categoryFacets.forEach((oldFacet: any) => {
                            if (newFacet.name === oldFacet.name && oldFacet.selected) {
                                newFacet.selected = true;
                            }
                        });
                    });

                    apiResult.typeFacets.forEach((newFacet: any) => {
                        typeFacets.forEach((oldFacet: any) => {
                            if (newFacet.name === oldFacet.name && oldFacet.selected) {
                                newFacet.selected = true;
                            }
                        });
                    });

                    const newBrands = apiResult.brandFacets.filter((facet: any) => facet.selected);
                    const newCategory = apiResult.categoryFacets.filter((facet: any) => facet.selected);
                    const newTypes = apiResult.typeFacets.filter((facet: any) => facet.selected);

                    /**
                     * api params
                     */
                    setProductBrand(newBrands.map((facet: any) => facet.name));
                    setProductCategory(newCategory.map((facet: any) => facet.value));
                    setDocumentType(newTypes.map((facet: any) => facet.name));

                    /**
                     * facets with selected:true from previous render
                     */
                    setCategoryFacets(apiResult.categoryFacets);
                    setBrandFacets(apiResult.brandFacets);
                    setTypeFacets(apiResult.typeFacets);
                }
            },
        });
    };

    React.useEffect(() => {
        if (!isLoadingDocuments) {
            loadData();
        }
    }, []);

    const changePage = (newPageIndex: number) => {
        setPage(newPageIndex);
    };

    // TODO ISC-11787 - make showMoreLimit configurable
    const showMoreLimit = 5;

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);

        setPageSize(newPageSize);
    };

    const addFilter = (
        facetFilterArr: string[],
        facet: XngFacetModel,
        setArr: any,
        title: string,
        isCategoryFilter?: boolean,
    ) => {
        const arr = [...facetFilterArr];
        const index = isCategoryFilter
            ? arr.findIndex((f: any) => f === facet.value)
            : arr.findIndex((f: any) => f === facet.name);

        if (index !== -1) {
            arr.splice(index, 1);
        } else {
            isCategoryFilter ? arr.push(facet.value || facet.name) : arr.push(facet.name);
        }

        setArr(arr);
        setFilterChanged({ name: title });
    };

    const onChangeFacet = (facet: XngFacetModel, title: string, facets: any) => {
        if (!facet.name) {
            return;
        }

        facet.selected = !facet.selected;

        switch (title) {
            case facetsTypes.categories:
                addFilter(productCategory, facet, setProductCategory, title, true);
                setCategoryFacets(facets);
                break;
            case facetsTypes.types:
                addFilter(documentType, facet, setDocumentType, title);
                setTypeFacets(facets);
                break;
            case facetsTypes.brands:
                addFilter(productBrand, facet, setProductBrand, title);
                setBrandFacets(facets);
                break;
            default:
                break;
        }
    };

    const jsonLd: any = {
        "@type": "CollectionPage",
        "@context": "https://schema.org",
        "@graph": documents.map(
            ({ documentType, fileDescription, fileName, fileUrl, icon, id, productBrands, productCategories }) => ({
                "@type": "DigitalDocument",
                name: fileName || fileDescription,
                description: `${productCategories && productCategories[0]}, ${fileDescription} ${documentType}`,
                // eslint-disable-next-line no-unsafe-optional-chaining
                url: `${url + urlForDetails?.url}?id=${id}`,
            }),
        ),
    };

    return (
        <StyledWrapper>
            <GridContainer gap={20}>
                <GridItem width={[12, 12, 12, 3, 3]}>
                    <StyledWrapper {...styles.filterSection}>
                        <Typography variant="h3" weight="normal">
                            {translate("Filters")}
                        </Typography>
                        <ProductListFiltersAccordionSection
                            title={facetsTypes.types}
                            facets={typeFacets}
                            onChangeFacetKnowledge={onChangeFacet}
                            showMoreLimit={showMoreLimit}
                            fromKnowledgeCenter={true}
                            onChangeFacet={() => {}}
                            expandByDefault={true}
                        />
                        <ProductListFiltersAccordionSection
                            title={facetsTypes.categories}
                            fromKnowledgeCenter={true}
                            facets={categoryFacets}
                            onChangeFacetKnowledge={onChangeFacet}
                            showMoreLimit={showMoreLimit}
                            onChangeFacet={() => {}}
                            expandByDefault={true}
                        />
                        <ProductListFiltersAccordionSection
                            title={facetsTypes.brands}
                            facets={brandFacets}
                            onChangeFacetKnowledge={onChangeFacet}
                            onChangeFacet={() => {}}
                            fromKnowledgeCenter={true}
                            showMoreLimit={showMoreLimit}
                            expandByDefault={true}
                        />
                    </StyledWrapper>
                </GridItem>
                <GridItem width={[12, 12, 12, 9, 9]} {...styles.tableStyle} align="top">
                    <StyledWrapper {...styles.paginationWrapper}>
                        <StyledWrapper {...styles.totalResultsStyle}>
                            <Typography
                                size="18px"
                                css={css`
                                    white-space: nowrap;
                                `}
                            >{`${totalResults} Items`}</Typography>
                        </StyledWrapper>
                        <Pagination
                            resultsCount={totalResults}
                            currentPage={page}
                            resultsPerPage={pageSize}
                            {...styles.topPaginationStyle}
                            resultsPerPageLabel="Results Per Page"
                            resultsPerPageOptions={pageSizeOptions}
                            onChangeResultsPerPage={changeResultsPerPage}
                            onChangePage={changePage}
                        />
                    </StyledWrapper>
                    {documents.length > 0 &&
                        documents.map(
                            ({
                                documentType,
                                fileDescription,
                                fileName,
                                fileUrl,
                                icon,
                                id,
                                productBrands,
                                productCategories,
                            }) => (
                                <GridContainer
                                    key={id}
                                    gap={0}
                                    css={css`
                                        margin-top: 15px;
                                        padding-bottom: 4px;
                                        border-bottom: 1px solid grey;
                                        flex-grow: 0;
                                    `}
                                >
                                    <GridItem width={[12, 12, 7, 7, 7]}>
                                        <GridContainer gap={0}>
                                            <GridItem
                                                width={2}
                                                css={css`
                                                    align-items: center;
                                                    justify-content: center;
                                                `}
                                            >
                                                <img src={icon} alt={fileName} style={{ maxHeight: "50px" }} />
                                            </GridItem>
                                            <GridItem
                                                width={10}
                                                css={css`
                                                    flex-direction: column;
                                                `}
                                                align="top"
                                            >
                                                <Typography
                                                    as="h3"
                                                    css={css`
                                                        font-size: 21px;
                                                        font-weight: bold;
                                                        margin-left: 4px;
                                                        max-width: 90%;
                                                    `}
                                                >
                                                    <Link href={`${urlForDetails?.url}?id=${id}`}>
                                                        <HTMLEllipsis
                                                            unsafeHTML={fileName || fileDescription}
                                                            maxLine="2"
                                                            ellipsisHTML="..."
                                                            basedOn="letters"
                                                        />
                                                    </Link>
                                                </Typography>
                                                <Typography
                                                    as="div"
                                                    css={css`
                                                        margin-left: 4px;
                                                    `}
                                                >
                                                    {documentType}
                                                </Typography>
                                            </GridItem>
                                        </GridContainer>
                                    </GridItem>
                                    <GridItem
                                        width={[12, 8, 5, 5, 5]}
                                        css={css`
                                            flex-direction: column;
                                        `}
                                    >
                                        {productBrands && (
                                            <>
                                                <Typography
                                                    as="div"
                                                    css={css`
                                                        @media only screen and (max-width: 769px) {
                                                            margin-top: 15px;
                                                        }
                                                    `}
                                                >
                                                    {productBrands[0]}
                                                </Typography>
                                                <hr />
                                            </>
                                        )}
                                        <Typography as="div" {...styles.tagsDiv}>
                                            <ListingCategories
                                                usage="categories"
                                                items={productCategories}
                                                doNotDisplayLink={true}
                                                viewMoreButtonAsText={true}
                                                categoriesNumber={3}
                                                fullWidth={true}
                                                view="inline"
                                            />
                                        </Typography>
                                    </GridItem>
                                </GridContainer>
                            ),
                        )}

                    {documents.length <= 0 && (
                        <StyledWrapper {...styles.centeringWrapper}>
                            {dataLoaded ? (
                                <Typography
                                    css={css`
                                        margin: auto;
                                    `}
                                    variant="h4"
                                    as="p"
                                >
                                    {translate("No documents found")}
                                </Typography>
                            ) : (
                                <LoadingSpinner {...styles.spinner} />
                            )}
                        </StyledWrapper>
                    )}
                    {documents.length > 0 && (
                        <StyledWrapper
                            css={css`
                                padding-top: 15px;
                                width: 100%;
                            `}
                        >
                            <Pagination
                                resultsCount={totalResults}
                                currentPage={page}
                                resultsPerPage={pageSize}
                                {...styles.bottomPaginationStyle}
                                resultsPerPageLabel="Results Per Page"
                                resultsPerPageOptions={pageSizeOptions}
                                onChangeResultsPerPage={changeResultsPerPage}
                                onChangePage={changePage}
                            />
                        </StyledWrapper>
                    )}
                </GridItem>
            </GridContainer>

            {documents.length > 0 && (
                // eslint-disable-next-line react/no-danger
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
            )}
        </StyledWrapper>
    );
};

export const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(KnowledgeCenter),
    definition,
};

export default widgetModule;
